import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { bulkUpdateFilms, getFornitori, getSupporti, getCondizioni, getGeneri, getCaseDiProduzione, getUbicazioni, getDistinctCasaDiDistribuzione, bulkUpdateFilmsByFilter, spostaUbicazioneMassivo } from '../services/api';

const BulkEditFilmModal = ({ show, onHide, selectedIds, onSuccess, filters, totalFiltered }) => {
  const [updates, setUpdates] = useState({});
  const [fieldsToUpdate, setFieldsToUpdate] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sposta ubicazione (agisce sulle righe di stock, non sulla sola scritta del film)
  const [spostaAttivo, setSpostaAttivo] = useState(false);
  const [spostaDa, setSpostaDa] = useState('');
  const [spostaA, setSpostaA] = useState('');
  const [spostaQuantita, setSpostaQuantita] = useState('1');
  const [spostaTutte, setSpostaTutte] = useState(false);
  const [esitoSposta, setEsitoSposta] = useState(null);

  // Data for dropdowns
  const [fornitori, setFornitori] = useState([]);
  const [supporti, setSupporti] = useState([]);
  const [condizioni, setCondizioni] = useState([]);
  const [generi, setGeneri] = useState([]);
  const [caseDiProduzioneOptions, setCaseDiProduzioneOptions] = useState([]); // Nuovo stato
  const [ubicazioniOptions, setUbicazioniOptions] = useState([]);             // Nuovo stato
  const [casaDiDistribuzioneOptions, setCasaDiDistribuzioneOptions] = useState([]); // Nuovo stato

  useEffect(() => {
    if (show) {
      // Fetch data for dropdowns when modal opens
      const fetchData = async () => {
        try {
          const [fornitoriRes, supportiRes, condizioniRes, generiRes, caseProdRes, ubicazioniRes, caseDistRes] = await Promise.all([
            getFornitori(),
            getSupporti(),
            getCondizioni(),
            getGeneri(),
            getCaseDiProduzione(),
            getUbicazioni(),
            getDistinctCasaDiDistribuzione(), // Fetch Case di Distribuzione
          ]);
          setFornitori(fornitoriRes.data.fornitori || []);
          setSupporti(['', ...supportiRes.data] || []);
          setCondizioni(condizioniRes.data || []);
          setGeneri(['', ...generiRes.data] || []);
          setCaseDiProduzioneOptions(['', ...caseProdRes.data] || []);
          setUbicazioniOptions(['', ...ubicazioniRes.data] || []);
          setCasaDiDistribuzioneOptions(['', ...caseDistRes.data] || []); // Imposta il nuovo stato

        } catch (err) {
          setError("Errore nel caricamento dei dati per i menu a tendina.");
        }
      };
      fetchData();
      
      // Reset state on open
      setUpdates({});
      setFieldsToUpdate({});
      setError(null);
      setSpostaAttivo(false);
      setSpostaDa('');
      setSpostaA('');
      setSpostaQuantita('1');
      setSpostaTutte(false);
      setEsitoSposta(null);
    }
  }, [show]);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFieldsToUpdate(prev => ({ ...prev, [name]: checked }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdates(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    const finalUpdates = {};
    for (const field in fieldsToUpdate) {
      if (fieldsToUpdate[field]) {
        if (updates[field] === '') { // Converti stringhe vuote in null per i campi opzionali
          finalUpdates[field] = null;
        } else {
          finalUpdates[field] = updates[field];
        }
      }
    }

    // Converte i campi booleani da stringhe a booleani reali
    const booleanFields = ['in_vetrina', 'versione_noleggio', 'versione_editoriale', 'fuori_catalogo'];
    for (const field of booleanFields) {
      if (field in finalUpdates && finalUpdates[field] !== null) {
        if (finalUpdates[field] === 'true') {
          finalUpdates[field] = true;
        } else if (finalUpdates[field] === 'false') {
          finalUpdates[field] = false;
        }
      }
    }

    // Converte i campi numerici da stringhe a numeri (gli input type="number" restituiscono sempre stringhe)
    const numericFields = ['prezzo_negozio_base', 'prezzo_negozio', 'prezzo_ibs', 'costo_acquisto', 'quantita', 'fornitore_id'];
    for (const field of numericFields) {
      if (field in finalUpdates && finalUpdates[field] !== null) {
        finalUpdates[field] = Number(finalUpdates[field]);
      }
    }

    if (Object.keys(finalUpdates).length === 0 && !spostaAttivo) {
      setError("Nessun campo selezionato per l'aggiornamento.");
      setLoading(false);
      return;
    }
    if (spostaAttivo) {
      if (!spostaA.trim()) {
        setError("Indica l'ubicazione di destinazione.");
        setLoading(false);
        return;
      }
      if (!spostaTutte && !(parseInt(spostaQuantita, 10) > 0)) {
        setError('La quantità da spostare deve essere almeno 1.');
        setLoading(false);
        return;
      }
    }

    try {
        if (Object.keys(finalUpdates).length > 0) {
            if (filters) {
                await bulkUpdateFilmsByFilter(filters, finalUpdates);
            } else {
                await bulkUpdateFilms(selectedIds, finalUpdates);
            }
        }
        if (spostaAttivo) {
            const payload = {
                ubicazione_a: spostaA.trim(),
                ubicazione_da: spostaDa.trim() || null,
                quantita: spostaTutte ? null : parseInt(spostaQuantita, 10),
                ...(filters ? { filters } : { film_ids: selectedIds }),
            };
            const res = await spostaUbicazioneMassivo(payload);
            onSuccess();
            if (res.data.saltati && res.data.saltati.length > 0) {
                // Resta aperta per mostrare cosa non e' stato spostato e perche'
                setEsitoSposta(res.data);
                return;
            }
            onHide();
            return;
        }
        onSuccess();
        onHide();
    } catch (err) {
      setError(err.response?.data?.detail || "Si è verificato un errore durante l'aggiornamento.");
    } finally {
      setLoading(false);
    }
  };

  const renderField = (name, label, type = 'text', options = []) => (
    <Form.Group as={Row} className="mb-3 align-items-center" controlId={`formBulk${name}`}>
      <Col sm={1}>
        <Form.Check
          type="checkbox"
          name={name}
          checked={fieldsToUpdate[name] || false}
          onChange={handleCheckboxChange}
        />
      </Col>
      <Col sm={3}>
        <Form.Label>{label}</Form.Label>
      </Col>
      <Col sm={8}>
        {type === 'select' ? (
          <Form.Select
            name={name}
            onChange={handleInputChange}
            disabled={!fieldsToUpdate[name]}
            value={updates[name] || ''}
          >
            <option value="">Seleziona...</option>
            {options.map((option, index) => (
              <option key={`${name}-${index}-${option.value ?? option}`} value={option.value ?? option}>
                {option.label || option}
              </option>
            ))}
          </Form.Select>
        ) : (
          <Form.Control
            type={type}
            name={name}
            value={updates[name] || ''}
            onChange={handleInputChange}
            disabled={!fieldsToUpdate[name]}
            step={type === 'number' ? '0.01' : undefined}
          />
        )}
      </Col>
    </Form.Group>
  );

  const title = filters 
    ? `Modifica Multipla Film (${totalFiltered} film filtrati)`
    : `Modifica Multipla Film (${selectedIds.length} selezionati)`;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Spunta la casella accanto ai campi che desideri aggiornare per tutti i film selezionati.</p>
        <hr />
        {error && <Alert variant="danger">{error}</Alert>}
        {esitoSposta && (
          <Alert variant="warning">
            <div className="fw-semibold">{esitoSposta.messaggio}</div>
            <div className="small">Non spostati:</div>
            <ul className="small mb-0">
              {esitoSposta.saltati.slice(0, 15).map(s => (
                <li key={s.id}>{s.titolo || `Film ${s.id}`}: {s.motivo}</li>
              ))}
              {esitoSposta.saltati.length > 15 && <li>… e altri {esitoSposta.saltati.length - 15}</li>}
            </ul>
          </Alert>
        )}
        
        <Form>
          {renderField('prezzo_negozio_base', 'Prezzo Base (ricalcola Negozio/IBS)', 'number')}
          {renderField('prezzo_negozio', 'Prezzo Negozio', 'number')}
          {renderField('prezzo_ibs', 'Prezzo IBS', 'number')}
          {renderField('costo_acquisto', 'Costo Acquisto', 'number')}
          {renderField('quantita', 'Quantità', 'number')}
          {renderField('supporto', 'Supporto', 'select', supporti)}
          {renderField('condizione', 'Condizione', 'select', condizioni)}
          {renderField('genere', 'Genere', 'select', generi)}
          {renderField('casa_di_produzione', 'Casa di Produzione', 'select', caseDiProduzioneOptions)} {/* Modificato qui */}
          {renderField('casa_di_distribuzione', 'Casa di Distribuzione', 'select', casaDiDistribuzioneOptions)}
          <div className="border rounded p-3 mb-3 bg-light">
            <Form.Check
              type="checkbox"
              id="bulk-sposta-attivo"
              label={<strong>Sposta ubicazione</strong>}
              checked={spostaAttivo}
              onChange={(e) => setSpostaAttivo(e.target.checked)}
            />
            <div className="small text-muted mb-2">
              Sposta lo stock da un'ubicazione all'altra: le altre ubicazioni del film restano com'erano.
              Con "Da" vuoto vengono spostati solo i film che hanno una sola ubicazione.
            </div>
            <Row className="g-2 align-items-end">
              <Col sm={4}>
                <Form.Label className="small mb-1">Da (ubicazione di partenza)</Form.Label>
                <Form.Control list="bulk-ubicazioni-list" value={spostaDa} onChange={(e) => setSpostaDa(e.target.value)} disabled={!spostaAttivo} placeholder="es. DVD USATI LIB" />
              </Col>
              <Col sm={4}>
                <Form.Label className="small mb-1">A (destinazione)</Form.Label>
                <Form.Control list="bulk-ubicazioni-list" value={spostaA} onChange={(e) => setSpostaA(e.target.value)} disabled={!spostaAttivo} placeholder="es. DVD USATI LIB - BUSTA" />
              </Col>
              <Col sm={2}>
                <Form.Label className="small mb-1">Quantità</Form.Label>
                <Form.Control type="number" min="1" value={spostaQuantita} onChange={(e) => setSpostaQuantita(e.target.value)} disabled={!spostaAttivo || spostaTutte} />
              </Col>
              <Col sm={2}>
                <Form.Check type="checkbox" id="bulk-sposta-tutte" label="Tutte" checked={spostaTutte} onChange={(e) => setSpostaTutte(e.target.checked)} disabled={!spostaAttivo} />
              </Col>
            </Row>
            <datalist id="bulk-ubicazioni-list">
              {ubicazioniOptions.filter(Boolean).map(u => <option key={u} value={u} />)}
            </datalist>
          </div>
          {renderField('fornitore_id', 'Fornitore', 'select', fornitori.map(f => ({ value: f.id, label: f.denominazione })))}
          {renderField('versione_noleggio', 'Versione Noleggio', 'select', [{ value: true, label: 'Sì' }, { value: false, label: 'No' }, { value: null, label: 'Nessuna Modifica' }])}
          {renderField('versione_editoriale', 'Versione Editoriale', 'select', [{ value: true, label: 'Sì' }, { value: false, label: 'No' }, { value: null, label: 'Nessuna Modifica' }])}
          {renderField('fuori_catalogo', 'Fuori Catalogo', 'select', [{ value: true, label: 'Sì' }, { value: false, label: 'No' }, { value: null, label: 'Nessuna Modifica' }])}
          {renderField('in_vetrina', 'In Vetrina', 'select', [{ value: true, label: 'Sì' }, { value: false, label: 'No' }, { value: null, label: 'Nessuna Modifica' }])}
        </Form>

      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {esitoSposta ? 'Chiudi' : 'Annulla'}
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading || !!esitoSposta}>
          {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Applica Modifiche'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkEditFilmModal;
